// Cart storage fallback, layer 2 (flag REACT_APP_CART_STORAGE_FALLBACK_ENABLED,
// design DESIGN-CART-STORAGE-TRIN3-2026-09-24 §1 "Fælles"): the IndexedDB
// shadow store behind the marker-gated shadow copy of the cart bucket.
//
// One database (kitmaker-cart-shadow, version 1), one object store (buckets,
// keyPath "key"); a record is the SAME v2 string CartContext would have
// written to localStorage. The store is opened lazily, on the first
// operation - never at mount. Nothing here is reached with the flag off.
//
// Availability policy (§0b S1 d):
//   hard failure (no indexedDB, open() throws synchronously - Firefox <= 114
//   private mode -, onerror, onblocked)  -> `unavailable` latches for the
//   page's lifetime, ONE console.warn, every operation rejects at once;
//   open timeout (1.5 s, Safari hangs)   -> the cached open is dropped and
//   operations reject at once WITHOUT a new open() until
//   allowRetryAfterTimeout() - the provider calls it at the start of each
//   quota episode, so at most one new open attempt per episode;
//   versionchange (another tab upgrades) -> close + drop the cached open,
//   the next operation reopens; no latch.
// Writes resolve on transaction.oncomplete, not request.onsuccess, so a
// resolved put is a committed put. Every operation has its own 1.5 s
// timeout; an operation timeout latches nothing.

export interface ShadowRecord {
  key: string;
  payload: string;
  nonce: string;
  writtenAt: number;
}

export interface ShadowStore {
  get(key: string): Promise<ShadowRecord | undefined>;
  put(record: ShadowRecord): Promise<void>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
  deleteAll(): Promise<void>;
  readonly unavailable: boolean;
  allowRetryAfterTimeout(): void;
  // Closes a cached connection (before deleteDatabase); a no-op otherwise.
  close(): void;
}

export const SHADOW_DB_NAME = "kitmaker-cart-shadow";
export const SHADOW_DB_VERSION = 1;
export const SHADOW_STORE_NAME = "buckets";
export const SHADOW_OP_TIMEOUT_MS = 1500;

export class ShadowUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShadowUnavailableError";
  }
}

export class ShadowTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShadowTimeoutError";
  }
}

// Rejects with a ShadowTimeoutError when `promise` has not settled within
// `ms`. The timer is cleared on settle, so it never keeps a test alive.
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label = "operation",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new ShadowTimeoutError(`${label} timed out after ${ms} ms`)),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function errorName(error: unknown): string {
  const name = (error as { name?: unknown } | null)?.name;
  return typeof name === "string" && name ? name : String(error);
}

// `indexedDB` can be missing (jsdom, very old WebViews) and reading it can
// itself throw in some private-mode builds - both count as a hard failure.
function getIndexedDb(): IDBFactory | undefined {
  try {
    return typeof indexedDB === "undefined" ? undefined : indexedDB;
  } catch {
    return undefined;
  }
}

export function createIndexedDbShadowStore(): ShadowStore {
  let unavailable = false;
  let openTimedOut = false;
  let warned = false;
  let dbPromise: Promise<IDBDatabase> | undefined;

  function markUnavailable(reason: string): ShadowUnavailableError {
    unavailable = true;
    dbPromise = undefined;
    if (!warned) {
      warned = true;
      console.warn("[cart-storage] IndexedDB shadow unavailable: %s", reason);
    }
    return new ShadowUnavailableError(
      `IndexedDB shadow unavailable: ${reason}`,
    );
  }

  function open(): Promise<IDBDatabase> {
    if (unavailable) {
      return Promise.reject(
        new ShadowUnavailableError("IndexedDB shadow unavailable"),
      );
    }
    if (openTimedOut) {
      return Promise.reject(
        new ShadowUnavailableError(
          "IndexedDB open timed out earlier this episode",
        ),
      );
    }
    if (dbPromise) return dbPromise;
    const idb = getIndexedDb();
    if (!idb) return Promise.reject(markUnavailable("indexedDB is missing"));
    let request: IDBOpenDBRequest;
    try {
      request = idb.open(SHADOW_DB_NAME, SHADOW_DB_VERSION);
    } catch (error) {
      return Promise.reject(markUnavailable(errorName(error)));
    }
    let timedOut = false;
    const opened = new Promise<IDBDatabase>((resolve, reject) => {
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(SHADOW_STORE_NAME)) {
          db.createObjectStore(SHADOW_STORE_NAME, { keyPath: "key" });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        if (timedOut) {
          // Nobody waits for this handle any more - do not keep it open.
          db.close();
          return;
        }
        db.onversionchange = () => {
          db.close();
          if (dbPromise === promise) dbPromise = undefined;
        };
        resolve(db);
      };
      request.onerror = () => {
        reject(markUnavailable(request.error?.name ?? "open failed"));
      };
      request.onblocked = () => {
        reject(markUnavailable("open blocked"));
      };
    });
    const promise: Promise<IDBDatabase> = withTimeout(
      opened,
      SHADOW_OP_TIMEOUT_MS,
      "IndexedDB open",
    ).catch(
      (error) => {
        if (dbPromise === promise) dbPromise = undefined;
        if (!unavailable && error instanceof ShadowTimeoutError) {
          timedOut = true;
          openTimedOut = true;
        }
        throw error;
      },
    );
    dbPromise = promise;
    return promise;
  }

  async function run<T>(
    mode: IDBTransactionMode,
    label: string,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await open();
    return withTimeout(
      new Promise<T>((resolve, reject) => {
        let transaction: IDBTransaction;
        try {
          transaction = db.transaction(SHADOW_STORE_NAME, mode);
        } catch (error) {
          // A closed connection (versionchange) throws InvalidStateError:
          // drop the cached handle so the next operation reopens.
          dbPromise = undefined;
          reject(error);
          return;
        }
        let result: T;
        const request = operation(transaction.objectStore(SHADOW_STORE_NAME));
        request.onsuccess = () => {
          result = request.result;
        };
        request.onerror = () => reject(request.error ?? new Error(label));
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error ?? new Error(label));
        transaction.onabort = () =>
          reject(transaction.error ?? new Error(`${label} aborted`));
      }),
      SHADOW_OP_TIMEOUT_MS,
      label,
    );
  }

  return {
    get unavailable() {
      return unavailable;
    },
    allowRetryAfterTimeout() {
      openTimedOut = false;
    },
    close() {
      const cached = dbPromise;
      dbPromise = undefined;
      if (cached) cached.then((db) => db.close()).catch(() => undefined);
    },
    get(key) {
      return run<ShadowRecord | undefined>("readonly", "IndexedDB get", (store) =>
        store.get(key) as IDBRequest<ShadowRecord | undefined>,
      );
    },
    put(record) {
      return run<IDBValidKey>("readwrite", "IndexedDB put", (store) =>
        store.put(record),
      ).then(() => undefined);
    },
    delete(key) {
      return run<undefined>("readwrite", "IndexedDB delete", (store) =>
        store.delete(key),
      ).then(() => undefined);
    },
    keys() {
      return run<IDBValidKey[]>("readonly", "IndexedDB keys", (store) =>
        store.getAllKeys(),
      ).then((keys) => keys.map((k) => String(k)));
    },
    deleteAll() {
      return run<undefined>("readwrite", "IndexedDB clear", (store) =>
        store.clear(),
      ).then(() => undefined);
    },
  };
}

// The one store the provider uses; tests inject an in-memory implementation.
let currentStore: ShadowStore | undefined;

export function getCartShadowStore(): ShadowStore {
  if (!currentStore) currentStore = createIndexedDbShadowStore();
  return currentStore;
}

export function setCartShadowStoreForTests(store: ShadowStore | undefined): void {
  currentStore = store;
}

// "Reset cart" hygiene: drops the whole shadow database. Resolves at once
// when IndexedDB is missing or the store is unavailable; the caller bounds
// the wait (withTimeout) - the marker keys removed synchronously beforehand
// are the primary guard, this is cleanup.
export function deleteShadowDatabase(): Promise<void> {
  const store = getCartShadowStore();
  store.close();
  if (store.unavailable) return Promise.resolve();
  const idb = getIndexedDb();
  if (!idb) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = idb.deleteDatabase(SHADOW_DB_NAME);
    } catch (error) {
      reject(error);
      return;
    }
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("deleteDatabase failed"));
    // onblocked: another tab holds a connection; the delete completes once
    // it closes. The caller's timeout decides how long to wait.
  });
}
